import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { BiTrash, BiPlus } from 'react-icons/bi';
import { css } from '@emotion/css';
import { toast } from 'react-toastify';
import { useRooftop, useUser } from '@store';
import { useBusinessHours } from '../hooks/useBusinessHours';
import { processClosures } from '../utils/businessHoursUtils';
import AddClosureModal from './AddClosureModal';

const LocationClosures: React.FC = () => {
  const rooftop = useRooftop();
  const user = useUser();
  const locationId = rooftop?.location?.id?.toString();

  const { businessHours, deleteBusinessHours, loading, deleting } = useBusinessHours(locationId);

  const [addModalOpen, setAddModalOpen] = React.useState(false);
  const closures = processClosures(businessHours);

  const handleDelete = async (closureId: string, closureName: string) => {
    if (!confirm(`Are you sure you want to delete "${closureName}"?`)) {
      return;
    }

    try {
      await deleteBusinessHours({
        variables: {
          id: closureId,
        },
      });
      toast.success('Closure deleted successfully');
    } catch (error: any) {
      console.error('Error deleting closure:', error);
      toast.error(`Failed to delete closure: ${error.message}`);
    }
  };

  if (!rooftop || !locationId) {
    return (
      <Paper className={paperStyles}>
        <Typography variant="body1" color="text.secondary">
          No location found for this customer.
        </Typography>
      </Paper>
    );
  }

  if (loading && closures.length === 0) {
    return (
      <Paper className={paperStyles}>
        <Typography variant="h6" gutterBottom>
          Location Closures
        </Typography>
        <LinearProgress />
      </Paper>
    );
  }

  return (
    <>
      <Paper className={paperStyles}>
        <Box className={headerStyles}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Location Closures
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Special dates when closed
            </Typography>
          </Box>
        </Box>

        <Box className={closuresListStyles}>
          {closures.map((closure) => (
            <Box key={closure.id} className={closureCardStyles}>
              <Box className={closureContentStyles}>
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    {closure.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {closure.date}
                  </Typography>
                  {closure.isRecurring && (
                    <Chip
                      label="All Day"
                      size="small"
                      className={allDayChipStyles}
                    />
                  )}
                </Box>

                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDelete(closure.id, closure.name)}
                  disabled={deleting}
                >
                  <BiTrash />
                </IconButton>
              </Box>
            </Box>
          ))}

          <Button
            variant="outlined"
            fullWidth
            startIcon={<BiPlus />}
            onClick={() => setAddModalOpen(true)}
            className={addButtonStyles}
          >
            Add Closure
          </Button>
        </Box>
      </Paper>

      <AddClosureModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        locationId={locationId}
      />
    </>
  );
};

// Styles
const paperStyles = css`
  padding: 2rem;
`;

const headerStyles = css`
  margin-bottom: 2rem;
`;

const closuresListStyles = css`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const closureCardStyles = css`
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 1.25rem 1.5rem;
  background-color: #fffbf0;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  }
`;

const closureContentStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const allDayChipStyles = css`
  margin-top: 0.5rem;
  height: 24px;
  font-size: 0.75rem;
  background-color: #ffd966;
  font-weight: 500;
`;

const addButtonStyles = css`
  margin-top: 0.5rem;
  border-style: dashed;
  border-width: 2px;
  text-transform: none;
  padding: 1rem;
  font-size: 1rem;

  &:hover {
    border-style: dashed;
    border-width: 2px;
    background-color: rgba(0, 0, 0, 0.02);
  }
`;

export default LocationClosures;
